import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AlertTriangle, Building2, Crosshair, LoaderCircle, MapPin, Search, WandSparkles } from "lucide-react";
import { MapContainer, TileLayer, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import api, { formatApiError } from "@/lib/api";
import Modal from "@/components/Modal";
import { useAuth } from "@/contexts/AuthContext";

const STATUS = {
    confirmed: { label: "Confirmada", color: "#15803D" },
    approximate: { label: "Aproximada", color: "#D97706" },
    manual: { label: "Manual", color: "#7C3AED" },
    pending: { label: "Pendiente", color: "#64748B" },
    ambiguous: { label: "Ambigua", color: "#DC2626" },
    error: { label: "Error", color: "#DC2626" },
    insufficient_address: { label: "Dirección insuficiente", color: "#475569" },
};

const OPERATIONAL_STATUS = {
    incident: { label: "Incidencia activa", color: "#DC2626" },
    service: { label: "Limpieza o servicio en curso", color: "#16A34A" },
    idle: { label: "Sin actividad", color: "#64748B" },
};

function markerIcon(property) {
    const color = OPERATIONAL_STATUS[property.operational_status]?.color || OPERATIONAL_STATUS.idle.color;
    return L.divIcon({
        className: "zentra-map-marker",
        html: `<span style="display:block;width:16px;height:16px;border-radius:999px;background:${color};border:3px solid white;box-shadow:0 1px 5px rgba(0,0,0,.45)"></span>`,
        iconSize: [18, 18],
        iconAnchor: [9, 9],
    });
}

function ClusteredMarkers({ items, onSelect, onOpenDetail }) {
    const map = useMap();

    useEffect(() => {
        const cluster = L.markerClusterGroup({ showCoverageOnHover: false, maxClusterRadius: 48 });
        const bounds = [];
        items.forEach((property) => {
            const latitude = Number(property.latitude);
            const longitude = Number(property.longitude);
            if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return;
            const marker = L.marker([latitude, longitude], { icon: markerIcon(property) });
            marker.on("click", () => onSelect(property));
            marker.bindTooltip(`${property.code} · ${property.name || "Sin nombre"}`, { direction: "top" });
            const popup = document.createElement("div");
            const title = document.createElement("strong");
            title.textContent = `${property.code} · ${property.name || "Sin nombre"}`;
            const address = document.createElement("p");
            address.textContent = property.raw_address || property.address || property.city || "Sin dirección";
            address.style.margin = "6px 0";
            const button = document.createElement("button");
            button.type = "button";
            button.textContent = "Abrir ficha de la vivienda";
            button.style.fontWeight = "600";
            button.style.color = "#0d6b4d";
            button.addEventListener("click", () => onOpenDetail(property.id));
            popup.append(title, address, button);
            marker.bindPopup(popup);
            cluster.addLayer(marker);
            bounds.push([latitude, longitude]);
        });
        map.addLayer(cluster);
        if (bounds.length && map.__zentraLocatedCount !== bounds.length) {
            map.fitBounds(bounds, { padding: [30, 30], maxZoom: 14 });
            map.__zentraLocatedCount = bounds.length;
        }
        return () => map.removeLayer(cluster);
    }, [items, map, onSelect, onOpenDetail]);
    return null;
}

export default function PropertyMap() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const [issues, setIssues] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [search, setSearch] = useState("");
    const [agency, setAgency] = useState("");
    const [city, setCity] = useState("");
    const [status, setStatus] = useState("");
    const [selected, setSelected] = useState(null);
    const [editing, setEditing] = useState(false);
    const [geocoding, setGeocoding] = useState({ enabled: false, provider: "disabled", ready: false });
    const [geocodingJob, setGeocodingJob] = useState(null);
    const [startingGeocoding, setStartingGeocoding] = useState(false);

    const load = useCallback(async (append = false, silent = false) => {
        if (!silent) setLoading(true);
        setError("");
        try {
            const skip = append ? items.length : 0;
            const { data } = await api.get("/map/properties", {
                params: { search: search || undefined, agency: agency || undefined, city: city || undefined, geocode_status: status || undefined, skip, limit: 2000 },
            });
            setItems((current) => append ? [...current, ...(data.items || [])] : (data.items || []));
            if (!append) {
                setSelected((current) => current
                    ? (data.items || []).find((item) => item.id === current.id) || current
                    : current);
            }
            setTotal(data.total || 0);
            setHasMore(Boolean(data.has_more));
            setIssues(data.geocoding_issues || 0);
            setGeocoding(data.geocoding || { enabled: false, provider: "disabled", ready: false });
            if (data.geocoding_job) setGeocodingJob(data.geocoding_job);
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            if (!silent) setLoading(false);
        }
    }, [agency, city, items.length, search, status]);

    useEffect(() => {
        const timer = setTimeout(() => load(false), 250);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [agency, city, search, status]);

    useEffect(() => {
        const interval = setInterval(() => load(false, true), 15000);
        return () => clearInterval(interval);
    }, [load]);

    useEffect(() => {
        if (!geocodingJob?.id || !["queued", "running"].includes(geocodingJob.status)) return undefined;
        const poll = async () => {
            try {
                const { data } = await api.get(`/map/geocode-jobs/${geocodingJob.id}`);
                setGeocodingJob(data);
                if (["completed", "failed"].includes(data.status)) load(false, true);
            } catch (err) {
                setError(formatApiError(err));
            }
        };
        const interval = setInterval(poll, 1500);
        return () => clearInterval(interval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [geocodingJob?.id, geocodingJob?.status]);

    const startGeocoding = async () => {
        if (!window.confirm("Se enviarán las direcciones pendientes al proveedor OpenCage. El proceso usa caché y puede tardar varios minutos. ¿Continuar?")) return;
        setStartingGeocoding(true);
        setError("");
        try {
            const { data } = await api.post("/map/geocode-jobs");
            setGeocodingJob(data);
            if (data.status === "completed") load(false);
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setStartingGeocoding(false);
        }
    };

    const agencies = useMemo(() => [...new Set(items.map((item) => item.agency).filter(Boolean))].sort(), [items]);
    const cities = useMemo(() => [...new Set(items.map((item) => item.city).filter(Boolean))].sort(), [items]);
    const located = useMemo(() => items.filter((item) => Number.isFinite(Number(item.latitude)) && Number.isFinite(Number(item.longitude))), [items]);

    const updateSelected = (updated) => {
        setItems((current) => current.map((item) => item.id === updated.id ? { ...item, ...updated } : item));
        setSelected((current) => current?.id === updated.id ? { ...current, ...updated } : current);
    };
    const openPropertyDetail = useCallback((propertyId) => navigate(`/propiedades/${propertyId}`), [navigate]);

    return (
        <div className="space-y-5" data-testid="property-map-page">
            <div className="flex items-end justify-between gap-3 flex-wrap">
                <div>
                    <p className="text-[11px] font-semibold tracking-widest uppercase text-zentra-accent-strong">Localización operativa</p>
                    <h2 className="font-display text-3xl font-bold text-zentra-ink mt-1">Mapa de viviendas</h2>
                    <p className="text-sm text-zentra-muted mt-1">{total} viviendas visibles · {located.length} con coordenadas cargadas</p>
                </div>
                <div className="flex items-center gap-2 flex-wrap justify-end">
                    <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                        <AlertTriangle size={16} /> {issues} incidencias de geocodificación
                    </div>
                    {["admin", "super_admin"].includes(user?.role) && (
                        <button
                            className="zentra-btn-primary"
                            onClick={startGeocoding}
                            disabled={!geocoding.ready || startingGeocoding || ["queued", "running"].includes(geocodingJob?.status) || issues === 0}
                            title={!geocoding.ready ? "Configura OpenCage en el backend para habilitar esta acción" : "Posicionar viviendas sin coordenadas"}
                            data-testid="start-property-geocoding"
                        >
                            {startingGeocoding || ["queued", "running"].includes(geocodingJob?.status)
                                ? <LoaderCircle size={16} className="animate-spin" />
                                : <WandSparkles size={16} />}
                            {!geocoding.ready ? "Geocodificación sin configurar" : "Posicionar pendientes"}
                        </button>
                    )}
                </div>
            </div>

            {geocodingJob && (
                <GeocodingProgress job={geocodingJob} />
            )}

            <div className="zentra-card-static p-3 grid gap-3 md:grid-cols-4">
                <label className="flex items-center gap-2 zentra-input md:col-span-1">
                    <Search size={14} className="text-zentra-muted" />
                    <input className="bg-transparent border-0 outline-none w-full" placeholder="Código o nombre" value={search} onChange={(event) => setSearch(event.target.value)} />
                </label>
                <select className="zentra-input" value={agency} onChange={(event) => setAgency(event.target.value)}>
                    <option value="">Todas las agencias</option>
                    {agencies.map((value) => <option key={value} value={value}>{value}</option>)}
                </select>
                <select className="zentra-input" value={city} onChange={(event) => setCity(event.target.value)}>
                    <option value="">Todos los municipios</option>
                    {cities.map((value) => <option key={value} value={value}>{value}</option>)}
                </select>
                <select className="zentra-input" value={status} onChange={(event) => setStatus(event.target.value)}>
                    <option value="">Todos los estados</option>
                    {Object.entries(STATUS).map(([key, value]) => <option key={key} value={key}>{value.label}</option>)}
                </select>
            </div>

            {error && <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error}</div>}

            <div className="grid gap-4 xl:grid-cols-[1fr_320px]">
                <div className="zentra-card-static overflow-hidden min-h-[620px] relative">
                    <MapContainer center={[36.7213, -4.4214]} zoom={9} className="h-[620px] w-full" scrollWheelZoom>
                        <TileLayer
                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                            referrerPolicy="strict-origin-when-cross-origin"
                            url={process.env.REACT_APP_MAP_TILE_URL || "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"}
                        />
                        <ClusteredMarkers items={located} onSelect={setSelected} onOpenDetail={openPropertyDetail} />
                    </MapContainer>
                    <div className="absolute left-3 bottom-3 z-[500] rounded-lg border border-zentra-border bg-white/95 shadow-md p-3 space-y-1.5" data-testid="map-legend">
                        <p className="text-[10px] font-semibold tracking-widest uppercase text-zentra-muted">Leyenda operativa</p>
                        {Object.entries(OPERATIONAL_STATUS).map(([key, value]) => (
                            <div key={key} className="flex items-center gap-2 text-xs text-zentra-ink"><span className="w-2.5 h-2.5 rounded-full" style={{ background: value.color }} />{value.label}</div>
                        ))}
                    </div>
                </div>

                <aside className="zentra-card-static p-4 h-fit space-y-4">
                    {selected ? (
                        <>
                            <div className="flex items-start justify-between gap-3">
                                <div>
                                    <span className="text-xs font-semibold tracking-wide text-zentra-accent-strong">{selected.code}</span>
                                    <h3 className="font-display text-xl font-semibold text-zentra-ink">{selected.name || "Sin nombre"}</h3>
                                </div>
                                <span className="w-3 h-3 rounded-full mt-1" style={{ background: OPERATIONAL_STATUS[selected.operational_status]?.color || OPERATIONAL_STATUS.idle.color }} />
                            </div>
                            <div className="space-y-2 text-sm">
                                <p className="flex gap-2"><Building2 size={15} className="mt-0.5 text-zentra-muted" />{selected.agency || "Sin agencia"}</p>
                                <p className="flex gap-2"><MapPin size={15} className="mt-0.5 text-zentra-muted" />{selected.raw_address || selected.address || "Sin dirección"}{selected.city ? `, ${selected.city}` : ""}</p>
                                <p className="font-medium" style={{ color: OPERATIONAL_STATUS[selected.operational_status]?.color }}>
                                    {OPERATIONAL_STATUS[selected.operational_status]?.label || OPERATIONAL_STATUS.idle.label}
                                </p>
                                <p className="text-zentra-muted">Localización: {STATUS[selected.geocode_status]?.label || "Pendiente"}</p>
                                {(selected.active_services || []).map((service) => (
                                    <p key={service.id} className="text-green-700">Servicio: {service.type || "limpieza"}{service.started_at ? ` · iniciado ${new Date(service.started_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}` : ""}</p>
                                ))}
                                {selected.open_incident_count > 0 && <p className="text-red-700">{selected.open_incident_count} incidencia(s) abierta(s)</p>}
                            </div>
                            <div className="flex flex-col gap-2">
                                <Link className="zentra-btn-primary justify-center" to={`/propiedades/${selected.id}`}>Abrir ficha</Link>
                                <button className="zentra-btn-ghost justify-center" onClick={() => setEditing(true)}><Crosshair size={15} /> Corregir posición</button>
                            </div>
                        </>
                    ) : (
                        <div className="py-8 text-center text-sm text-zentra-muted">
                            <MapPin className="mx-auto mb-2" /> Selecciona un marcador para ver la vivienda.
                        </div>
                    )}
                    <div className="border-t border-zentra-border pt-3 space-y-2">
                        {Object.entries(OPERATIONAL_STATUS).map(([key, value]) => (
                            <div key={key} className="flex items-center gap-2 text-xs text-zentra-muted"><span className="w-2.5 h-2.5 rounded-full" style={{ background: value.color }} />{value.label}</div>
                        ))}
                    </div>
                </aside>
            </div>

            {hasMore && <button disabled={loading} className="zentra-btn-ghost mx-auto" onClick={() => load(true)}>{loading ? "Cargando…" : "Cargar más viviendas"}</button>}
            <ManualLocationModal open={editing} property={selected} onClose={() => setEditing(false)} onSaved={updateSelected} />
        </div>
    );
}

function GeocodingProgress({ job }) {
    const total = Number(job.total) || 0;
    const processed = Number(job.processed) || 0;
    const percent = total ? Math.min(100, Math.round((processed / total) * 100)) : 100;
    const running = ["queued", "running"].includes(job.status);
    return (
        <div className={`rounded-lg border p-3 ${job.status === "failed" ? "border-red-200 bg-red-50" : "border-zentra-border bg-white"}`} data-testid="geocoding-progress">
            <div className="flex items-center justify-between gap-3 text-sm">
                <span className="font-medium text-zentra-ink flex items-center gap-2">
                    {running && <LoaderCircle size={15} className="animate-spin" />}
                    {running ? "Posicionando viviendas" : job.status === "failed" ? "Geocodificación detenida" : "Geocodificación terminada"}
                </span>
                <span className="text-zentra-muted">{processed}/{total} · {percent}%</span>
            </div>
            <div className="h-2 rounded-full bg-zentra-bg mt-2 overflow-hidden">
                <div className="h-full bg-zentra-accent transition-all" style={{ width: `${percent}%` }} />
            </div>
            <p className="text-xs text-zentra-muted mt-2">
                {job.located || 0} posicionadas · {job.ambiguous || 0} ambiguas · {job.failed || 0} sin coincidencia · {job.skipped || 0} incompletas
            </p>
            {job.error && <p className="text-xs text-red-700 mt-1">{job.error}</p>}
        </div>
    );
}

function ManualLocationModal({ open, property, onClose, onSaved }) {
    const [latitude, setLatitude] = useState("");
    const [longitude, setLongitude] = useState("");
    const [note, setNote] = useState("");
    const [error, setError] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open && property) {
            setLatitude(property.latitude ?? "");
            setLongitude(property.longitude ?? "");
            setNote("");
            setError("");
        }
    }, [open, property]);

    const save = async () => {
        setSaving(true);
        setError("");
        try {
            const { data } = await api.patch(`/map/properties/${property.id}/location`, { latitude: Number(latitude), longitude: Number(longitude), note });
            onSaved(data);
            onClose();
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal open={open} onClose={onClose} title="Corrección manual de posición" subtitle="Introduce coordenadas revisadas. El cambio quedará auditado.">
            <div className="space-y-4">
                {error && <div className="text-sm text-red-700">{error}</div>}
                <div className="grid grid-cols-2 gap-3">
                    <label><span className="zentra-label">Latitud</span><input type="number" step="any" className="zentra-input w-full" value={latitude} onChange={(event) => setLatitude(event.target.value)} /></label>
                    <label><span className="zentra-label">Longitud</span><input type="number" step="any" className="zentra-input w-full" value={longitude} onChange={(event) => setLongitude(event.target.value)} /></label>
                </div>
                <label><span className="zentra-label">Motivo o referencia</span><textarea className="zentra-input w-full" rows={3} value={note} onChange={(event) => setNote(event.target.value)} /></label>
                <div className="flex justify-end gap-2"><button className="zentra-btn-ghost" onClick={onClose}>Cancelar</button><button className="zentra-btn-primary" disabled={saving || latitude === "" || longitude === ""} onClick={save}>{saving ? "Guardando…" : "Guardar posición"}</button></div>
            </div>
        </Modal>
    );
}
